import React, { type FunctionComponent } from "react";
import { Alert, Button, Space, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ClientImportAggregation } from "@fitness-app/data-models/entities/ProductClientToImport";

import { ImportSummaryCounters } from "../../ProductImports/ImportSummaryCounters";
import { useProductImport } from "../../ProductImports/useProductImports";
import { type TableModel } from "../ImportClientsModal";

interface OwnProps {
  tableModel: TableModel[];
  productId: string;
  /** Set once the batch is accepted by the backend; null while the request is in flight. */
  importId: string | null;
  close: () => void;
}

type Props = OwnProps;

/**
 * Last step of the stepper: the live summary of the batch that was just queued
 * (TRE-2110). Used to be a spinner that closed itself after 3 s and pointed at
 * the notification drawer — the outcome (how many were duplicates, which rows
 * failed) was never shown anywhere.
 */
const AddClientsToProduct: FunctionComponent<Props> = ({ tableModel, productId, importId, close }) => {
  const { t } = useTranslation(["products", "common"]);
  const { data, isError } = useProductImport(productId, importId);
  const aggregation = data?.aggregation;
  const finished = aggregation?.status === ClientImportAggregation.Finished;

  if (isError) {
    // The batch was accepted; only reading its summary failed. The footer is
    // hidden on this step, so the way out has to be here.
    return (
      <div className="mx-auto flex min-h-[300px] max-w-3xl flex-col justify-center gap-6 py-6">
        <Alert type="error" showIcon message={t("import.summary.loadError")} />
        <Space className="justify-center">
          <Button type="primary" onClick={close}>
            {t("common:button.close")}
          </Button>
        </Space>
      </div>
    );
  }

  if (!importId || !aggregation) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center">
        <Space direction="vertical" align="center" size={16}>
          <Typography.Title level={4} style={{ padding: "0 20px" }}>
            {t("import.startInfo", { count: tableModel.length })}
          </Typography.Title>
          <Spin />
        </Space>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[300px] max-w-3xl flex-col justify-center gap-6 py-6">
      <Typography.Title level={4} className="!mb-0 text-center">
        {finished ? t("import.summary.finishedTitle") : t("import.summary.inProgressTitle")}
      </Typography.Title>
      <ImportSummaryCounters aggregation={aggregation} />
      {finished && aggregation.failed > 0 ? (
        <Alert type="warning" showIcon message={t("import.summary.failedHint", { count: aggregation.failed })} />
      ) : null}
      {finished && aggregation.skipped > 0 ? (
        <Alert type="info" showIcon message={t("import.summary.skippedHint", { count: aggregation.skipped })} />
      ) : null}
      <Space className="justify-center">
        <Link to={`/products/${productId}/clients/imports/${importId}`} onClick={close}>
          <Button>{t("import.summary.openDetails")}</Button>
        </Link>
        <Button type="primary" onClick={close}>
          {t("common:button.close")}
        </Button>
      </Space>
    </div>
  );
};

export default AddClientsToProduct;
